import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import TutorialPage from './TutorialPage';
import FormPage from './FormPage';
import MainApp from './App';
import { Flex } from './shared/Flex';
import Logo from './Logo';

const Container = styled(Flex)`
  height: 100%;
  width: 100%;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: #F8F8FF;
  position: relative;
  overflow: hidden;
`;

const LogoContainer = styled(Flex)`
  position: absolute;
  top: 20px;
  left: 30px;
  z-index: 2;
  width: 100%;
  justify-content: start;
  align-items: center;
`;

const ContentContainer = styled.div<{ isMeasurementStep?: boolean }>`
  width: 100%;
  height: calc(100%); /* Match navigation height */
  overflow: ${({ isMeasurementStep }) => isMeasurementStep ? 'hidden' : 'auto'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ isMeasurementStep }) => isMeasurementStep ? 'flex-start' : 'flex-start'};
  padding: ${({ isMeasurementStep }) => isMeasurementStep ? '0' : '20%'};
`;

const NavigationButtons = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  z-index: 100;
  justify-content: space-between;
  align-items: center;
  background-color: #F8F8FF;
`;

const StepDotsContainer = styled(Flex)`
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const NavigationSection = styled(Flex)`
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: 16px;

  &:first-child {
    justify-content: flex-start;
  }

  &:last-child {
    justify-content: flex-end;
  }
`;

const StepDot = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ active }) => active ? '#007AFF' : '#D1D1D6'};
  transition: background-color 0.2s ease;
`;

const Button = styled.button`
  padding: 16px 32px;
  border-radius: 6px;
  border: none;
  background-color: #004995;
  color: white;
  font-size: 1.1em;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0066d2;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

enum WizardStep {
  TUTORIAL = 0,
  FORM = 1,
  MEASUREMENT = 2
}

const WizardContainer = () => {
  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.TUTORIAL);
  const [isTutorialComplete, setIsTutorialComplete] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);

  const handleNext = useCallback(() => {
    if (currentStep === WizardStep.TUTORIAL) {
      setIsTutorialComplete(true);
    }
    if (currentStep < WizardStep.MEASUREMENT) {
      setCurrentStep(prev => (prev + 1) as WizardStep);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > WizardStep.TUTORIAL) {
      setCurrentStep(prev => (prev - 1) as WizardStep);
    }
  }, [currentStep]);

  const handleFormComplete = useCallback(() => {
    setIsFormComplete(true);
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case WizardStep.TUTORIAL:
        return (
          <ContentContainer>
            <TutorialPage onComplete={() => setIsTutorialComplete(true)} />
          </ContentContainer>
        );
      case WizardStep.FORM:
        return (
          <ContentContainer>
            <FormPage
              onSettingsComplete={handleFormComplete}
            />
          </ContentContainer>
        );
      case WizardStep.MEASUREMENT:
        return (
          <ContentContainer isMeasurementStep>
            <MainApp />
          </ContentContainer>
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case WizardStep.TUTORIAL:
        return true;
      case WizardStep.FORM:
        return isFormComplete;
      case WizardStep.MEASUREMENT:
        return true;
      default:
        return false;
    }
  };

  return (
    <Container>
      <LogoContainer>
        <Logo/>
      </LogoContainer>
      {renderStep()}
      <NavigationButtons>
        <NavigationSection>
          {currentStep > WizardStep.TUTORIAL && (
            <Button onClick={handleBack}>Regresar</Button>
          )}
        </NavigationSection>
        {/* <StepDotsContainer>
          {[WizardStep.TUTORIAL, WizardStep.FORM, WizardStep.MEASUREMENT].map((step) => (
            <StepDot key={step} active={currentStep === step} />
          ))}
        </StepDotsContainer> */}
        <NavigationSection>
          {currentStep < WizardStep.MEASUREMENT && (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Siguiente
            </Button>
          )}
        </NavigationSection>
      </NavigationButtons>
    </Container>
  );
};

export default WizardContainer;
